/**
 * PRE-188/190 — the product-owner view of the end-to-end learner journey, from
 * sign-up to graduation: diagrams plus step tables, written in product language
 * rather than code terms.
 *
 * The content comes from D1 over `GET /v1/admin/blueprint` so this screen stays
 * a pure renderer. It was previously imported from `@prepzio/blueprint` and
 * compiled into this bundle, which meant the deployed panel could show content
 * older than the document it claimed to render. Never author here — the document
 * is edited through the API, and the Traceability screen derives its matrix from
 * exactly the same rows.
 */
import { type FlowStatus, type Traceability } from '@halilural/rtmkit';
import {
  Alert,
  Badge,
  Card,
  Code,
  Grid,
  Group,
  List,
  Loader,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import { createContext, useContext } from 'react';

import { Mermaid } from '../components/Mermaid';
import { SeededLock, SEEDED_FILES } from '../components/SeededLock.js';
import { useBlueprint } from '../hooks/use-product-document';
import { useT } from '../i18n';
import type { BlueprintResponse } from '../lib/api';
import { errorMessage } from '../lib/ui';

/** The loaded document, so the presentational parts below don't each re-fetch.
 *  Non-null by construction: the provider only renders once the query resolved. */
const DocContext = createContext<BlueprintResponse | null>(null);

function useDoc(): BlueprintResponse {
  const doc = useContext(DocContext);
  if (!doc) throw new Error('UserFlow document used outside its provider');
  return doc;
}

/** Free-form `extras` sections the engine passes through; the panel owns their
 *  shape, so it decodes them here rather than pretending they are typed data. */
type FlowExtras = {
  decision: string[];
  coverage: {
    covered: string[];
    missing: string[];
    evidence: string;
    approach: { text: string; status: FlowStatus }[];
    warning: string;
  };
};

const EMPTY_EXTRAS: FlowExtras = {
  decision: [],
  coverage: { covered: [], missing: [], evidence: '', approach: [], warning: '' },
};

/** Consistent ready / to-build / broken pill used across every table. */
function StatusBadge({ status }: { status: FlowStatus }) {
  const { labels } = useDoc();
  return (
    <Badge
      color={labels.statusColor[status]}
      variant={status === 'todo' ? 'outline' : 'light'}
      radius="sm"
      size="sm"
    >
      {labels.status[status]}
    </Badge>
  );
}

/**
 * Traceability anchors for a step. Kept compact — the full matrix with search
 * and filters lives on the Traceability screen; here it is just enough to see
 * whether a claim is backed by anything.
 */
function TraceCell({ trace }: { trace?: Traceability }) {
  const t = useT();
  const brs = trace?.br ?? [];
  const code = trace?.code ?? [];
  const tests = trace?.tests ?? [];

  if (brs.length === 0 && code.length === 0) {
    return (
      <Badge color="red" variant="outline" radius="sm" size="xs">
        {t('userFlow.untraceable')}
      </Badge>
    );
  }

  return (
    <Stack gap={2}>
      {brs.length > 0 ? (
        <Group gap={4}>
          {brs.map((br) => (
            <Badge key={br} color="gray" variant="light" radius="sm" size="xs">
              {br}
            </Badge>
          ))}
        </Group>
      ) : null}
      {code.map((path) => (
        <Text key={path} size="xs" ff="monospace" c="dimmed">
          {path}
        </Text>
      ))}
      {tests.length > 0 ? (
        <Text size="xs" c="teal">
          {t('userFlow.testCount', { count: tests.length })}
        </Text>
      ) : null}
    </Stack>
  );
}

/**
 * Step editor. Only the fields that are the operator's call: what the step does,
 * whether it works today, and what backs that claim. The step id and its phase
 * are not editable here — the matrix and every stored coverage verdict key on
 * the id, so changing one is a migration, not an edit.
 */

/**
 * PRE-81: the rule editor, added for the anchors.
 *
 * Rules were the only requirement kind with nowhere to put a code anchor, which is
 * why every `R-<nn>` came back from the semantic review as "no anchor" while
 * claiming to be built. The rule text stays the product owner's words; what is new
 * is the pair of anchor fields that let the review actually read the invariant.
 */
function Figure({ caption, chart }: { caption: string; chart: string }) {
  return (
    <Card withBorder padding="md" radius="md" mb="md">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="sm">
        {caption}
      </Text>
      <Mermaid chart={chart} />
    </Card>
  );
}

/**
 * Loads the document, then renders it. Split from the content below so every
 * presentational part can assume the document exists rather than guarding.
 */
export function UserFlowScreen() {
  const t = useT();
  const { data, isPending, error } = useBlueprint();

  if (isPending) {
    return (
      <Group justify="center" py="xl">
        <Loader />
      </Group>
    );
  }

  if (error || !data) {
    return (
      <Alert color="red" title={t('userFlow.loadFailed')}>
        {errorMessage(error)}
      </Alert>
    );
  }

  return (
    <DocContext.Provider value={data}>
      <UserFlowContent />
    </DocContext.Provider>
  );
}

function UserFlowContent() {
  const t = useT();
  const doc = useDoc();
  // Local aliases keep the markup below reading as the document it renders.
  const FLOW_META = doc.meta;
  const FLOW_ACTORS = doc.actors;
  const FLOW_PHASES = doc.phases;
  const FLOW_RULES = doc.rules;
  const FLOW_SLICES = doc.slices;
  const FLOW_OVERVIEW = doc.overview;
  const FLOW_PERSONAS = doc.personas;
  const FLOW_BACKLOG = doc.backlog;
  const FLOW_LANE_LABEL = doc.labels.lane;
  const extras = doc.extras as Partial<FlowExtras>;
  const FLOW_DECISION = extras.decision ?? EMPTY_EXTRAS.decision;
  const FLOW_COVERAGE = extras.coverage ?? EMPTY_EXTRAS.coverage;

  return (
    <Stack gap="xl">
      <div>
        <Text c="dimmed" size="sm">
          {t('userFlow.intro')}
        </Text>
      </div>

      <Card withBorder padding="sm" radius="md">
        <Group gap="lg" wrap="wrap">
          <Text size="xs" c="dimmed">
            <b>{t('userFlow.owner')}</b> {FLOW_META.owner}
          </Text>
          <Text size="xs" c="dimmed">
            <b>{t('userFlow.verifiedAt')}</b> {FLOW_META.verifiedAt}{' '}
            {t('userFlow.staleAfter', { days: FLOW_META.staleAfterDays })}
          </Text>
          <Text size="xs" c="dimmed">
            <b>{t('userFlow.adr')}</b> <Code>{FLOW_META.adr}</Code>
          </Text>
          <Text size="xs" c="dimmed">
            <b>{t('userFlow.ruleCatalog')}</b> <Code>{FLOW_META.ruleCatalog}</Code>
          </Text>
        </Group>
      </Card>

      <Alert color="indigo" variant="light" icon={<IconInfoCircle size={18} />}>
        <Text fw={700} mb="xs">
          {t('userFlow.currentDecision', { date: FLOW_META.decisionDate ?? '—' })}
        </Text>
        <List spacing="xs" size="sm">
          {FLOW_DECISION.map((item) => (
            <List.Item key={item}>{item}</List.Item>
          ))}
        </List>
      </Alert>

      <div>
        <Title order={3} mb="xs">
          {t('userFlow.readingGuide')}
        </Title>
        <Text c="dimmed" size="sm" mb="md">
          {t('userFlow.readingGuideHint')}
        </Text>
        <Grid gap="sm" mb="md">
          {FLOW_ACTORS.map((actor) => (
            <Grid.Col key={actor.name} span={{ base: 12, sm: 6, lg: 4 }}>
              <Card withBorder padding="sm" radius="md" h="100%">
                <Text fw={700} size="sm">
                  {actor.name}
                </Text>
                <Text size="xs" c="dimmed">
                  {actor.desc}
                </Text>
                <Badge mt="xs" size="xs" radius="sm" variant="light" color="gray">
                  {FLOW_LANE_LABEL[actor.lane]}
                </Badge>
              </Card>
            </Grid.Col>
          ))}
        </Grid>
        <Group gap="xs">
          <StatusBadge status="ready" />
          <Text size="xs" c="dimmed">
            {t('userFlow.worksToday')}
          </Text>
          <StatusBadge status="todo" />
          <Text size="xs" c="dimmed">
            {t('userFlow.toBuild')}
          </Text>
          <StatusBadge status="gap" />
          <Text size="xs" c="dimmed">
            {t('userFlow.brokenToday')}
          </Text>
        </Group>
      </div>

      <div>
        <Title order={3} mb="xs">
          {t('userFlow.personas')}
        </Title>
        <Text c="dimmed" size="sm" mb="md">
          {t('userFlow.personasHint')}
        </Text>
        <Grid gap="sm">
          {FLOW_PERSONAS.map((persona) => (
            <Grid.Col key={persona.name} span={{ base: 12, sm: 6, lg: 3 }}>
              <Card withBorder padding="md" radius="md" h="100%">
                <Text fw={800}>{persona.name}</Text>
                <Text size="xs" c="dimmed" mb="xs">
                  {persona.role}
                </Text>
                <Text size="sm" mb="sm">
                  {persona.desc}
                </Text>
                <Text size="xs" c="indigo" fw={600}>
                  {t('userFlow.day0', { signal: persona.signal })}
                </Text>
              </Card>
            </Grid.Col>
          ))}
        </Grid>
      </div>

      <div>
        <Title order={3} mb="xs">
          {t('userFlow.bigPicture')}
        </Title>
        <Text c="dimmed" size="sm" mb="md">
          {t('userFlow.bigPictureHint')}
        </Text>
        {FLOW_OVERVIEW ? (
          <>
            <Figure caption={FLOW_OVERVIEW.caption} chart={FLOW_OVERVIEW.chart} />
            <Alert color="teal" variant="light">
              {FLOW_OVERVIEW.summary}
            </Alert>
          </>
        ) : (
          <Text c="dimmed" size="sm">
            {t('userFlow.noOverview')}
          </Text>
        )}
      </div>

      {FLOW_PHASES.map((phase, index) => (
        <div key={phase.num}>
          <Title order={3} mb="xs">
            {t('userFlow.phaseTitle', {
              index: String(index + 3).padStart(2, '0'),
              num: phase.num,
              title: phase.title,
            })}
          </Title>
          <Text c="dimmed" size="sm" mb="md">
            {phase.sub}
          </Text>

          <Figure caption={phase.caption} chart={phase.chart} />

          <Card withBorder padding={0} radius="md">
            <Table.ScrollContainer minWidth={980}>
              <Table striped highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={60}>{t('userFlow.colId')}</Table.Th>
                    <Table.Th w={110}>{t('userFlow.colWho')}</Table.Th>
                    <Table.Th>{t('userFlow.colWhat')}</Table.Th>
                    <Table.Th>{t('userFlow.colRuleNote')}</Table.Th>
                    <Table.Th w={220}>{t('userFlow.colTrace')}</Table.Th>
                    <Table.Th w={110}>{t('common.status')}</Table.Th>
                    <Table.Th w={50} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {phase.steps.map((step) => (
                    <Table.Tr key={step.id}>
                      <Table.Td>
                        <Code>{step.id}</Code>
                      </Table.Td>
                      <Table.Td fw={600}>{step.who}</Table.Td>
                      <Table.Td>{step.what}</Table.Td>
                      <Table.Td c="dimmed">{step.rule}</Table.Td>
                      <Table.Td>
                        <TraceCell trace={step.trace} />
                      </Table.Td>
                      <Table.Td>
                        <StatusBadge status={step.status} />
                      </Table.Td>
                      <Table.Td>
                        {/* PRE-125: the journey is authored in the repo. */}
                        <SeededLock file={SEEDED_FILES.steps} what={t('userFlow.flowStep')} />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Card>

          {phase.notes?.map((note) => (
            <Alert
              key={note.title}
              mt="md"
              color={note.kind === 'warn' ? 'red' : 'indigo'}
              variant="light"
              icon={
                note.kind === 'warn' ? (
                  <IconAlertTriangle size={18} />
                ) : (
                  <IconInfoCircle size={18} />
                )
              }
              title={note.title}
            >
              {note.text}
            </Alert>
          ))}
        </div>
      ))}

      <div>
        <Title order={3} mb="xs">
          {t('userFlow.rulesTitle', {
            index: String(FLOW_PHASES.length + 3).padStart(2, '0'),
          })}
        </Title>
        <Text c="dimmed" size="sm" mb="md">
          {t('userFlow.rulesHint')}
        </Text>
        <Card withBorder padding={0} radius="md">
          <Table.ScrollContainer minWidth={720}>
            <Table striped highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={190}>{t('userFlow.colRule')}</Table.Th>
                  <Table.Th>{t('userFlow.colSays')}</Table.Th>
                  <Table.Th>{t('userFlow.colWhy')}</Table.Th>
                  <Table.Th w={130}>{t('userFlow.colBusinessRule')}</Table.Th>
                  <Table.Th w={170}>{t('userFlow.colCodeAnchor')}</Table.Th>
                  <Table.Th w={60} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {FLOW_RULES.map((rule) => (
                  <Table.Tr key={rule.rule}>
                    <Table.Td fw={700}>{rule.rule}</Table.Td>
                    <Table.Td>{rule.says}</Table.Td>
                    <Table.Td c="dimmed">{rule.why}</Table.Td>
                    <Table.Td>
                      {(rule.br ?? []).length > 0 ? (
                        <Group gap={4}>
                          {(rule.br ?? []).map((br) => (
                            <Badge key={br} color="gray" variant="light" radius="sm" size="xs">
                              {br}
                            </Badge>
                          ))}
                        </Group>
                      ) : (
                        <Badge color="red" variant="outline" radius="sm" size="xs">
                          {t('userFlow.notEnforced')}
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {rule.code.length > 0 ? (
                        <Stack gap={2}>
                          {rule.code.map((anchor) => (
                            <Text key={anchor} size="xs" ff="monospace" c="dimmed">
                              {anchor}
                            </Text>
                          ))}
                        </Stack>
                      ) : (
                        // A rule with no anchor is invisible to the semantic review:
                        // it lands as "no anchor" on every run, however confidently
                        // the document says it is built.
                        <Badge color="orange" variant="outline" radius="sm" size="xs">
                          {t('userFlow.noAnchor')}
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <SeededLock file={SEEDED_FILES.rules} what={t('userFlow.productRule')} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      </div>

      <div>
        <Title order={3} mb="xs">
          {t('userFlow.coverageTitle', {
            index: String(FLOW_PHASES.length + 4).padStart(2, '0'),
          })}
        </Title>
        <Text c="dimmed" size="sm" mb="md">
          {t('userFlow.coverageHint')}
        </Text>
        <Grid gap="sm">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card withBorder padding="md" radius="md" h="100%">
              <Text size="xs" tt="uppercase" fw={700} c="red" mb="sm">
                {t('userFlow.inCatalogToday')}
              </Text>
              <Group gap="xs" mb="md">
                {FLOW_COVERAGE.covered.map((item) => (
                  <Badge key={item} color="teal" variant="light" radius="sm">
                    {item}
                  </Badge>
                ))}
              </Group>
              <Text size="sm" c="dimmed" mb="xs">
                {t('userFlow.missingFromCatalog')}
              </Text>
              <Group gap="xs" mb="md">
                {FLOW_COVERAGE.missing.map((item) => (
                  <Badge key={item} color="red" variant="outline" radius="sm">
                    {item}
                  </Badge>
                ))}
              </Group>
              <Text size="sm" c="red">
                {FLOW_COVERAGE.evidence}
              </Text>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card withBorder padding="md" radius="md" h="100%">
              <Text size="xs" tt="uppercase" fw={700} c="indigo" mb="sm">
                {t('userFlow.approachTitle')}
              </Text>
              <Text size="sm" mb="md">
                {t('userFlow.approachBody')}
              </Text>
              <Stack gap="xs">
                {FLOW_COVERAGE.approach.map((item, index) => (
                  <Group key={item.text} gap="xs" wrap="nowrap" align="flex-start">
                    <Text size="sm" c="dimmed" w={16}>
                      {index + 1}
                    </Text>
                    <Text size="sm" style={{ flex: 1 }}>
                      {item.text}
                    </Text>
                    <StatusBadge status={item.status} />
                  </Group>
                ))}
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>
        <Alert
          mt="md"
          color="red"
          variant="light"
          icon={<IconAlertTriangle size={18} />}
          title={t('userFlow.coverageWarning')}
        >
          {FLOW_COVERAGE.warning}
        </Alert>
      </div>

      <div>
        <Title order={3} mb="xs">
          {t('userFlow.backlogTitle', {
            index: String(FLOW_PHASES.length + 5).padStart(2, '0'),
          })}
        </Title>
        <Text c="dimmed" size="sm" mb="md">
          {t('userFlow.backlogHint')}
        </Text>
        <Card withBorder padding={0} radius="md">
          <Table.ScrollContainer minWidth={720}>
            <Table striped highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={60}>{t('userFlow.colId')}</Table.Th>
                  <Table.Th>{t('userFlow.colStep')}</Table.Th>
                  <Table.Th w={110}>{t('common.status')}</Table.Th>
                  <Table.Th>{t('userFlow.colNote')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {FLOW_BACKLOG.map((item) => (
                  <Table.Tr key={item.id}>
                    <Table.Td>
                      <Code>{item.id}</Code>
                    </Table.Td>
                    <Table.Td fw={600}>{item.step}</Table.Td>
                    <Table.Td>
                      <StatusBadge status={item.status} />
                    </Table.Td>
                    <Table.Td c="dimmed">{item.note}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
        <Title order={4} mt="xl" mb="xs">
          {t('userFlow.slices')}
        </Title>
        <Text c="dimmed" size="sm" mb="md">
          {t('userFlow.slicesHint')}
        </Text>
        <Stack gap="sm">
          {FLOW_SLICES.map((slice) => (
            <Card key={slice.id} withBorder padding="md" radius="md">
              <Group justify="space-between" mb="xs" wrap="nowrap" align="flex-start">
                <div>
                  <Text fw={700}>{slice.title}</Text>
                  <Text size="sm" c="dimmed">
                    {slice.outcome}
                  </Text>
                </div>
                <Group gap={4} wrap="nowrap">
                  {slice.items.map((id) => (
                    <Badge key={id} variant="light" color="indigo" radius="sm" size="sm">
                      {id}
                    </Badge>
                  ))}
                </Group>
              </Group>
              <Text size="sm">{slice.rationale}</Text>
            </Card>
          ))}
        </Stack>
      </div>
    </Stack>
  );
}
